import { Heading, useDisclosure } from '@chakra-ui/react';
import { IAccountRespSchema } from '@monetize/types/app';
import { getErrorMessage } from '@monetize/utils/core';
import { useCallback, useRef, useState } from 'react';
import { useMergeAccounts } from '../../../api/accountsService';
import {
  MAlert,
  MButton,
  MCenterModal,
  MFlex,
  MStack,
} from '../../../components/Monetize';
import { getAccountDetailRoute } from '../../../constants/routes';
import { useToast } from '../../../services/toast';
import { useRelatedRecordValues } from './AccountMerge.hooks';
import { AccountMergeComparisonTable } from './AccountMergeComparisonTable';
import { AccountMergeConfirmationModal } from './AccountMergeConfirmationModal';
import { AccountToKeep } from './AccountMergeTable.types';

interface AccountMergeModalProps {
  account: IAccountRespSchema;
  onClose: (navigateTo?: string) => void;
}

export const AccountMergeModal = ({
  account: mainAccount,
  onClose,
}: AccountMergeModalProps) => {
  const { addToast } = useToast();
  const {
    open: isConfirmModalOpen,
    onClose: onConfirmModalClose,
    onOpen: onConfirmModalOpen,
  } = useDisclosure();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otherAccount, setOtherAccount] = useState<IAccountRespSchema | null>(
    null,
  );

  const [accountToKeep, setAccountToKeep] = useState<AccountToKeep>(
    AccountToKeep.MAIN,
  );

  const relatedRecordValuesRef =
    useRef<ReturnType<typeof useRelatedRecordValues>>();

  const handleRelatedValuesChange = useCallback(
    (relatedRecordValues: ReturnType<typeof useRelatedRecordValues>) => {
      relatedRecordValuesRef.current = relatedRecordValues;
    },
    [],
  );

  const { mutateAsync: doMergeAccounts } = useMergeAccounts();

  const handleSaveAccountMerge = async (params: {
    sourceAccountId: string;
    targetAccountId: string;
    retainSourcePaymentGatewayAccount: boolean;
  }) => {
    try {
      await doMergeAccounts(params);
      addToast({
        summary: 'Success',
        detail: 'Accounts merged successfully',
        severity: 'success',
      });
      handleClose(getAccountDetailRoute(params.targetAccountId));
    } catch (ex) {
      setErrorMessage(
        `There was a problem merging the accounts. ${getErrorMessage(ex)}`,
      );
      handleClose();
    }
  };

  function handleClose(navigateTo?: string) {
    onConfirmModalClose();
    onClose(navigateTo);
  }

  if (isConfirmModalOpen && otherAccount) {
    // We keep the target account - these are not backwards
    // even thought it appears so at first glance
    const [targetAccount, sourceAccount] =
      accountToKeep === AccountToKeep.MAIN
        ? [mainAccount, otherAccount]
        : [otherAccount, mainAccount];

    const relatedRecordValues = relatedRecordValuesRef.current;
    const [targetAccountRecords, sourceAccountRecords] =
      accountToKeep === AccountToKeep.MAIN
        ? [
            relatedRecordValues?.mainAccountRecords,
            relatedRecordValues?.otherAccountRecords,
          ]
        : [
            relatedRecordValues?.otherAccountRecords,
            relatedRecordValues?.mainAccountRecords,
          ];

    return (
      <AccountMergeConfirmationModal
        sourceAccount={sourceAccount}
        targetAccount={targetAccount}
        sourceAccountRecords={sourceAccountRecords}
        targetAccountRecords={targetAccountRecords}
        onCancel={onConfirmModalClose}
        onConfirm={handleSaveAccountMerge}
      />
    );
  }

  return (
    <MCenterModal
      isOpen
      onClose={() => handleClose()}
      size="full"
      modalContentProps={{ 'data-testid': 'account-merge-modal' } as any}
      modalTitle={
        <MFlex flexDirection="column" w="100%">
          <Heading as="h1" fontSize="2xl" mb={2}>
            Merge Accounts
          </Heading>
          <MAlert type="info">
            All related records will be moved from the archived account to the
            remaining account.
          </MAlert>
          {errorMessage && (
            <MAlert type="error" mt={4}>
              {errorMessage}
            </MAlert>
          )}
        </MFlex>
      }
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton onClick={() => handleClose()} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            variant="primary"
            disabled={!otherAccount}
            onClick={() => onConfirmModalOpen()}
            minW="auto"
          >
            Merge Accounts
          </MButton>
        </MStack>
      )}
    >
      <AccountMergeComparisonTable
        mainAccount={mainAccount}
        otherAccount={otherAccount}
        accountToKeep={accountToKeep}
        onOtherAccountChange={setOtherAccount}
        onAccountToKeepChange={setAccountToKeep}
        onRelatedValuesChange={handleRelatedValuesChange}
      />
    </MCenterModal>
  );
};
